import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Empty, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { VinInspectionItem } from '../../features/inspections';
import { findInspectionPanelKey, getInspectionPanelKey } from '../../features/inspections/inspectionUtils';
import { inspectionApi } from '../../features/inspections/inspectionAPI';
import DefectSideBar from '../../features/inspections/components/DefectSideBar';
import InspectionDetails from '../../features/inspections/components/InspectionDetails';
import InspectionMediaModal from '../../features/inspections/components/InspectionMediaModal';
import { useInspectionMediaSession } from '../../features/inspections/hooks/useInspectionMediaSession';
import { getDefaultVisibleTabs, type InspectionTabKey } from '../../features/inspections/inspectionTabs';
import Loader from '../../components/Loader';
import type { ApiError } from '../../types/api/network';

type VinDetailsMode = 'page' | 'drawer';

interface VinDetailsProps {
  vin?: string;
  mode?: VinDetailsMode;
  visibleTabs?: InspectionTabKey[];
  refreshKey?: number;
  readOnlyDamage?: boolean;
  initialInspectionId?: string;
  initialInspectionDateTime?: string;
  onStateChange?: (state: VinDetailsState) => void;
}

export interface VinDetailsState {
  data?: {
    vin?: string;
    make?: string;
    model?: string;
    year?: number;
  };
  loading: boolean;
  error: string | null;
}

function VinDetails({
  vin,
  mode = 'page',
  visibleTabs,
  refreshKey = 0,
  readOnlyDamage = false,
  initialInspectionId,
  initialInspectionDateTime,
  onStateChange,
}: VinDetailsProps) {
  const [activePanels, setActivePanels] = useState<string[]>([]);
  const [dismissedError, setDismissedError] = useState(false);
  const appliedInitialPanelRef = useRef<string | null>(null);
  const isDrawerMode = mode === 'drawer';
  const inspectionTabs = visibleTabs ?? getDefaultVisibleTabs(mode);

  const {
    data: inspection,
    error: queryError,
    isLoading,
    isFetching,
    refetch,
  } = inspectionApi.useGetInspectionsByVinQuery(vin || '', {
    skip: !vin,
  });

  const {
    mediaInspection,
    modalState,
    annotations,
    setAnnotations,
    mediaSourceTab,
    createStillFrame,
    downloadAsset,
    openMedia,
    closeMedia,
    forceCloseMedia,
    refreshAfterAnnotationChange,
    handleImageResolved,
  } = useInspectionMediaSession({ refetchInspections: refetch });

  const loading = vin ? isLoading || isFetching : false;
  const baseError = !vin ? 'VIN is required' : ((queryError as ApiError | undefined)?.message ?? null);
  const error = dismissedError ? null : baseError;

  const handleRefresh = () => {
    setDismissedError(false);
    refetch();
  };

  const handleExpandChange = (panelKey: string, expanded: boolean) => {
    setActivePanels(prev => {
      if (expanded) {
        return prev.includes(panelKey) ? prev : [...prev, panelKey];
      }
      return prev.filter(key => key !== panelKey);
    });
  };

  // Sort inspection items by dateTime in descending order (latest first)
  const sortedItems = useMemo(() => {
    if (!inspection?.items) return [];

    return [...inspection.items].sort((a, b) => {
      return new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime();
    });
  }, [inspection?.items]);

  useEffect(() => {
    setDismissedError(false);
  }, [baseError]);

  useEffect(() => {
    if (!sortedItems.length) {
      return;
    }

    const selectionKey = `${vin ?? ''}|${initialInspectionId ?? ''}|${initialInspectionDateTime ?? ''}`;
    if (appliedInitialPanelRef.current === selectionKey) {
      return;
    }

    const panelKey = findInspectionPanelKey(sortedItems, {
      inspectionId: initialInspectionId,
      dateTime: initialInspectionDateTime,
    });

    setActivePanels(panelKey ? [panelKey] : []);
    appliedInitialPanelRef.current = selectionKey;
  }, [sortedItems, initialInspectionId, initialInspectionDateTime, vin]);

  useEffect(() => {
    if (!vin || refreshKey === 0) return;
    refetch();
  }, [refreshKey, vin, refetch]);

  const vinData = inspection
    ? {
        vin: inspection.vin,
        make: inspection.make,
        model: inspection.model,
        year: inspection.year,
      }
    : undefined;

  useEffect(() => {
    onStateChange?.({
      data: {
        vin: vinData?.vin,
        make: vinData?.make,
        model: vinData?.model,
        year: vinData?.year,
      },
      loading,
      error,
    });
  }, [vinData?.vin, vinData?.make, vinData?.model, vinData?.year, loading, error, onStateChange]);

  return (
    <>
      {isDrawerMode ? null : <DefectSideBar loading={loading} heatMapData={inspection ? sortedItems : []} />}

      {loading && <Loader />}

      {error && !loading && (
        <div className="flex flex-col items-center justify-center p-8">
          <Alert
            message="Error"
            description={
              <div className="flex flex-col items-center gap-4">
                <span>{error}</span>
                <Button type="primary" icon={<ReloadOutlined />} onClick={handleRefresh} className="mt-2">
                  Retry
                </Button>
              </div>
            }
            type="error"
            showIcon
            closable
            onClose={() => setDismissedError(true)}
            className="w-full max-w-md"
          />
        </div>
      )}

      {!loading && !error && inspection?.items.length === 0 && <Empty description="No inspections found for this VIN" className="mt-8" />}

      {!loading && !error && inspection && inspection.items.length > 0 && (
        <div className="space-y-2">
          {sortedItems.map((item: VinInspectionItem, index: number) => {
            const panelKey = getInspectionPanelKey(item, index);
            return (
              <InspectionDetails
                key={panelKey}
                inspection={{ ...item, ...(vinData ?? {}) }}
                index={index}
                totalInspections={sortedItems.length}
                expandedPanels={activePanels}
                onExpandChange={handleExpandChange}
                visibleTabs={inspectionTabs}
                onOpenMedia={openMedia}
              />
            );
          })}
        </div>
      )}

      {mediaInspection && (
        <InspectionMediaModal
          open={modalState.isOpen}
          onClose={closeMedia}
          modalState={modalState}
          inspection={mediaInspection}
          annotations={annotations}
          setAnnotations={setAnnotations}
          createStillFrame={createStillFrame}
          downloadAsset={downloadAsset}
          refreshAfterAnnotationChange={refreshAfterAnnotationChange}
          disableAnnotation={readOnlyDamage}
          activeTab={mediaSourceTab}
          onCreateSuccess={forceCloseMedia}
          onImageResolved={handleImageResolved}
        />
      )}
    </>
  );
}

export default VinDetails;
